import { ApolloProvider } from '@apollo/client/react'
import Router from 'next/router'
import React, { useState } from 'react'
import getApolloClient from '../apollo-client'

/**
 * Counts the REAL navigations (non-shallow) started in this tab. The provider
 * mints a new client when it moves — which lands in the same `_app` render as
 * the new page, because `routeChangeStart` fires before that render — so every
 * page still starts on an empty cache, exactly as before.
 *
 * What changed (2026-09-26): the client used to be rebuilt on EVERY `_app`
 * render, and `_app` re-renders on every router update — shallow ones
 * included. A shallow `Router.replace` (a card tap writing drawer + scroll
 * context, a map pan, an admin queue selecting its next row) therefore threw
 * the whole cache away and dropped every live query to `loading` with no data
 * for a round trip: the directory floor collapsed ~3,000px under the reader's
 * thumb (CLS 0.86 twice per tap), and the admin queues lost the rows they had
 * just written into the cache by hand. Shallow updates now keep the cache.
 * docs/performance/interaction-latency.md → "Every router update throws the
 * Apollo cache away".
 */
let navigationEpoch = 0
if (typeof window !== 'undefined') {
  Router.events.on(
    'routeChangeStart',
    (_url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) navigationEpoch += 1
    }
  )
}

/**
 * The signed-in identity changed (login, logout, account switch): the NEXT
 * `_app` render — the next router update of any kind, shallow included — gets
 * a fresh client, which is what every router update did before 2026-09-26.
 * `logout()` never clears the store itself, so without this a previous
 * user's cached rows could now outlive shallow updates on the same page.
 * Deferred on purpose: minting the client right away would blank every live
 * query on the page the reader is looking at.
 */
export const discardApolloCacheOnNextUpdate = (): void => {
  navigationEpoch += 1
}

const ApolloProviderLoaded: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [current, setCurrent] = useState(() => ({
    epoch: navigationEpoch,
    client: getApolloClient()
  }))
  let { client } = current
  if (current.epoch !== navigationEpoch) {
    // Adjusting state while rendering (react.dev "storing information from
    // previous renders"): the new page's first render already gets the new
    // client, so its queries never start on the previous page's cache.
    client = getApolloClient()
    setCurrent({ epoch: navigationEpoch, client })
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default React.memo(ApolloProviderLoaded)
